import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, CircularProgress, Stack, Paper, FormControl, InputLabel,
  Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, TextField, Alert, Switch, FormControlLabel, RadioGroup,
  Radio, FormLabel,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

import { adminAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const GESELLSCHAFTSFORMEN = ['GmbH', 'AG', 'UG (haftungsbeschränkt)', 'GmbH & Co. KG', 'KG', 'OHG', 'GbR', 'Einzelunternehmen (e.K.)', 'e.V.', 'Sonstige'];
const SALUTATIONS = ['Herr', 'Frau', 'Herr Dr.', 'Frau Dr.', 'Herr Prof.', 'Frau Prof.'];
const INBOUND_TYPES = ['Bestellannahme', 'Helpdesk / Technische Hotline', 'Sekretariatsservice', 'Reservierung / Ticketing', 'Notfallservice / 24h-Hotline', 'Sonstiges'];
const OUTBOUND_TYPES = ['Telesales (Verkauf am Telefon)', 'Terminvereinbarung', 'Leadmanagement / Leadgenerierung', 'Adressqualifizierung', 'Kundenzufriedenheitsbefragung', 'Marktforschung / Mystery Calls', 'Kundenrückgewinnung', 'Sonstiges'];
const SERVICE_HOUR_OPTIONS = ['Mo. – Fr. normale Bürozeiten (9–18 Uhr)', 'Mo. – Fr. + Samstag', 'Mo. – Fr. + Wochenende', '24 Stunden / 7 Tage pro Woche', 'Individuell / Projektabhängig'];
const EMPLOYEE_COUNT_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1.000', '1.000+'];
const CALLS_PER_UNIT_OPTIONS = ['Stunde', 'Tag', 'Woche', 'Monat'];
const NEARSHORING_OPTIONS = ['Ja', 'Nein', 'Abhängig von Preis-Leistung', 'k.A.'];

const STATUS_COLOR: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning', approved: 'success', rejected: 'error',
};
const STATUS_LABEL: Record<string, string> = {
  pending: 'Ausstehend', approved: 'Genehmigt', rejected: 'Abgelehnt',
};

const AdminInquiriesPage: React.FC = () => {
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [approveDecision, setApproveDecision] = useState('approved');
  const [purchases, setPurchases] = useState<any[]>([]);
  const { showSuccess, showError } = useToast();

  const setField = (field: string, value: any) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  const defaultCreateForm: Record<string, any> = {
    title: '', description: '', contact_name: '', company_name: '', contact_email: '', contact_phone: '',
    salutation: '', society_form: '', position: '', zip_code: '', city: '', region: 'Deutschland',
    strasse: '', land: 'Deutschland', homepage: '',
    direction: 'inbound', customer_segment: 'B2B', recording_required: false,
    inbound_type: '', outbound_type: '', outbound_address_source: 'customer',
    call_volume: '', calls_per_unit: 'Monat', service_hours: '', languages: 'Deutsch',
    budget: '', quality_requirements: '',
    nearshoring: '', order_probability: '', additional_services: '', employee_count: '',
    project_remuneration: '', project_start: '', project_end: '', industry: '',
    avg_call_duration: '', address_volume: '', addresses_per: 'Monat',
    lead_price: '', purchase_limit: '4', status: 'approved', portal_note: '',
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<Record<string, any>>(defaultCreateForm);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateField = (field: string, value: any) =>
    setCreateForm((prev) => ({ ...prev, [field]: value }));

  const handleCreate = async () => {
    if (!createForm.title || !createForm.description || !createForm.contact_email) {
      showError('Titel, Beschreibung und E-Mail sind erforderlich');
      return;
    }
    setIsCreating(true);
    try {
      await adminAPI.createInquiry(createForm);
      showSuccess('Auftrag erstellt');
      setCreateOpen(false);
      setCreateForm(defaultCreateForm);
      load();
    } catch {
      showError('Fehler beim Erstellen des Auftrags');
    } finally {
      setIsCreating(false);
    }
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      const res = await adminAPI.getInquiries(params);
      setInquiries(res.data.data);
    } catch {
      showError('Fehler beim Laden der Anfragen');
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, showError]);

  useEffect(() => { load(); }, [load]);

  const handleOpen = async (inq: any) => {
    try {
      const [detailRes, purchasesRes] = await Promise.all([
        adminAPI.getInquiry(inq.id),
        adminAPI.getInquiryPurchases(inq.id),
      ]);
      const data = detailRes.data.data;
      setSelected(data);
      setEditData({
        ...data,
        lead_price: data.lead_price ?? '',
        purchase_limit: data.purchase_limit ?? '4',
        portal_note: data.portal_note ?? '',
        calls_per_unit: data.calls_per_unit ?? 'Monat',
        land: data.land ?? 'Deutschland',
        addresses_per: data.addresses_per ?? 'Monat',
        company_name: data.company_name ?? '',
      });
      setPurchases(purchasesRes.data.data || []);
      setApproveDecision('approved');
      setDialogOpen(true);
    } catch {
      showError('Fehler beim Laden der Anfrage');
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      await adminAPI.updateInquiry(selected.id, editData);
      showSuccess('Änderungen gespeichert');
      setDialogOpen(false);
      load();
    } catch {
      showError('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      await adminAPI.updateInquiry(selected.id, editData);
      await adminAPI.approveInquiry(selected.id, {
        approved: approveDecision === 'approved',
        lead_price: editData.lead_price ? parseFloat(editData.lead_price) : null,
        purchase_limit: editData.purchase_limit ? parseInt(editData.purchase_limit) : null,
        portal_note: editData.portal_note,
      });
      showSuccess(approveDecision === 'approved' ? 'Anfrage genehmigt' : 'Anfrage abgelehnt');
      setDialogOpen(false);
      load();
    } catch {
      showError('Fehler beim Verarbeiten der Anfrage');
    } finally {
      setIsSaving(false);
    }
  };

  const SectionTitle = ({ children, mt = 1 }: { children: string; mt?: number }) => (
    <Grid size={{ xs: 12 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt }}>
        {children}
      </Typography>
      <Divider sx={{ mb: 2 }} />
    </Grid>
  );

  const renderProjectTypeSection = (data: Record<string, any>, onChange: (f: string, v: any) => void) => (
    <>
      <SectionTitle>1. Art des Projekts</SectionTitle>
      <Grid size={{ xs: 12 }}>
        <FormControl component="fieldset">
          <FormLabel sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.85rem' }}>Callcenter wird beauftragt, um Anrufe…</FormLabel>
          <RadioGroup row value={data.direction || 'inbound'} onChange={(e) => onChange('direction', e.target.value)}>
            <FormControlLabel value="inbound" control={<Radio size="small" />} label="Entgegenzunehmen (Inbound)" />
            <FormControlLabel value="outbound" control={<Radio size="small" />} label="Aktiv zu tätigen (Outbound / Rebound)" />
            <FormControlLabel value="both" control={<Radio size="small" />} label="Beides (Inbound & Outbound)" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {(data.direction === 'inbound' || data.direction === 'both') && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Art des Inbound</InputLabel>
            <Select value={data.inbound_type || ''} onChange={(e) => onChange('inbound_type', e.target.value)} label="Art des Inbound">
              <MenuItem value="">–</MenuItem>
              {INBOUND_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      )}
      {(data.direction === 'outbound' || data.direction === 'both') && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Art des Outbound</InputLabel>
              <Select value={data.outbound_type || ''} onChange={(e) => onChange('outbound_type', e.target.value)} label="Art des Outbound">
                <MenuItem value="">–</MenuItem>
                {OUTBOUND_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl component="fieldset">
              <FormLabel sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.85rem' }}>Wer liefert die Adressen für den Outbound?</FormLabel>
              <RadioGroup row value={data.outbound_address_source || 'customer'} onChange={(e) => onChange('outbound_address_source', e.target.value)}>
                <FormControlLabel value="customer" control={<Radio size="small" />} label="Werden vom Auftraggeber gestellt" />
                <FormControlLabel value="callcenter" control={<Radio size="small" />} label="Sollen vom Callcenter gestellt werden" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </>
      )}
      <Grid size={{ xs: 12 }}>
        <FormControl component="fieldset">
          <FormLabel sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.85rem' }}>Kundensegment</FormLabel>
          <RadioGroup row value={data.customer_segment || 'B2B'} onChange={(e) => onChange('customer_segment', e.target.value)}>
            <FormControlLabel value="B2B" control={<Radio size="small" />} label="B2B – Geschäftskunden" />
            <FormControlLabel value="B2C" control={<Radio size="small" />} label="B2C – Privatkunden" />
            <FormControlLabel value="B2B+B2C" control={<Radio size="small" />} label="Beides" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={<Switch size="small" checked={!!data.recording_required} onChange={(e) => onChange('recording_required', e.target.checked)} />}
          label={<Typography variant="body2">Inkl. Gesprächsmitschnitt & Analyse</Typography>}
        />
      </Grid>
    </>
  );

  const renderProjectDetailsSection = (data: Record<string, any>, onChange: (f: string, v: any) => void) => (
    <>
      <SectionTitle>2. Projektdetails</SectionTitle>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small"
          label={data.direction === 'outbound' ? 'Menge zu bearbeitender Adressen' : 'Anrufvolumen'}
          value={data.call_volume || ''} onChange={(e) => onChange('call_volume', e.target.value)}
          placeholder={data.direction === 'outbound' ? 'z.B. 500' : 'z.B. 1.000'} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{data.direction === 'outbound' ? 'Adressen pro' : 'Anrufe pro'}</InputLabel>
          <Select value={data.calls_per_unit || 'Monat'} onChange={(e) => onChange('calls_per_unit', e.target.value)} label="Anrufe pro">
            {CALLS_PER_UNIT_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Servicezeiten</InputLabel>
          <Select value={data.service_hours || ''} onChange={(e) => onChange('service_hours', e.target.value)} label="Servicezeiten">
            <MenuItem value="">Bitte wählen</MenuItem>
            {SERVICE_HOUR_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Sprachen" value={data.languages || ''} onChange={(e) => onChange('languages', e.target.value)} placeholder="z.B. Deutsch, Englisch" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Geschätztes Budget (EUR/Monat)" type="number" value={data.budget || ''} onChange={(e) => onChange('budget', e.target.value)} inputProps={{ min: 0, step: 100 }} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth size="small" multiline rows={2} label="Qualitätssicherung & Anforderungen" value={data.quality_requirements || ''} onChange={(e) => onChange('quality_requirements', e.target.value)} placeholder="z.B. ISO-Zertifizierung, DSGVO-Compliance, Skript vorhanden…" />
      </Grid>
    </>
  );

  const renderEnrichmentSection = (data: Record<string, any>, onChange: (f: string, v: any) => void) => (
    <>
      <SectionTitle>3. Admin-Anreicherung</SectionTitle>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Nearshoring</InputLabel>
          <Select value={data.nearshoring || ''} onChange={(e) => onChange('nearshoring', e.target.value)} label="Nearshoring">
            <MenuItem value="">–</MenuItem>
            {NEARSHORING_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Anzahl Mitarbeiter</InputLabel>
          <Select value={data.employee_count || ''} onChange={(e) => onChange('employee_count', e.target.value)} label="Anzahl Mitarbeiter">
            <MenuItem value="">–</MenuItem>
            {EMPLOYEE_COUNT_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Durchschn. Gesprächslänge" value={data.avg_call_duration || ''} onChange={(e) => onChange('avg_call_duration', e.target.value)} placeholder="z.B. 2 Minuten" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Auftragswahrscheinlichkeit" value={data.order_probability || ''} onChange={(e) => onChange('order_probability', e.target.value)} placeholder="z.B. Abhängig von Preis-Leistung" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Projektvergütung" value={data.project_remuneration || ''} onChange={(e) => onChange('project_remuneration', e.target.value)} placeholder="z.B. verhandelbar – für Vorschläge offen" />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth size="small" label="Zusatzleistungen" value={data.additional_services || ''} onChange={(e) => onChange('additional_services', e.target.value)} placeholder="z.B. keine / Chat, E-Mail, Virtuelle Warteschleife" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Projektstart" value={data.project_start || ''} onChange={(e) => onChange('project_start', e.target.value)} placeholder="z.B. März/April 2025" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Projektende" value={data.project_end || ''} onChange={(e) => onChange('project_end', e.target.value)} placeholder="z.B. langfristige Zusammenarbeit" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Branche" value={data.industry || ''} onChange={(e) => onChange('industry', e.target.value)} placeholder="z.B. Softwareunternehmen" />
      </Grid>
    </>
  );

  const renderDescriptionSection = (data: Record<string, any>, onChange: (f: string, v: any) => void) => (
    <>
      <SectionTitle>4. Projektbeschreibung & Standort</SectionTitle>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth size="small" label="Projekttitel *" value={data.title || ''} onChange={(e) => onChange('title', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth size="small" multiline rows={4} label="Projektbeschreibung *" value={data.description || ''} onChange={(e) => onChange('description', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField fullWidth size="small" label="PLZ" value={data.zip_code || ''} onChange={(e) => onChange('zip_code', e.target.value)} inputProps={{ maxLength: 10 }} />
      </Grid>
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField fullWidth size="small" label="Ort" value={data.city || ''} onChange={(e) => onChange('city', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Land / Region</InputLabel>
          <Select value={data.region || 'Deutschland'} onChange={(e) => onChange('region', e.target.value)} label="Land / Region">
            <MenuItem value="Deutschland">Deutschland</MenuItem>
            <MenuItem value="Österreich">Österreich</MenuItem>
            <MenuItem value="Schweiz">Schweiz</MenuItem>
            <MenuItem value="EU-Ausland">EU-Ausland</MenuItem>
            <MenuItem value="International">International</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </>
  );

  const renderContactSection = (data: Record<string, any>, onChange: (f: string, v: any) => void) => (
    <>
      <SectionTitle>5. Kontaktdaten</SectionTitle>
      <Grid size={{ xs: 12, sm: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Anrede</InputLabel>
          <Select value={data.salutation || ''} onChange={(e) => onChange('salutation', e.target.value)} label="Anrede">
            <MenuItem value="">–</MenuItem>
            {SALUTATIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField fullWidth size="small" label="Name (Ansprechpartner) *" value={data.contact_name || ''} onChange={(e) => onChange('contact_name', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Firmenname" value={data.company_name || ''} onChange={(e) => onChange('company_name', e.target.value)} placeholder="z.B. Mustermann GmbH" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Position im Unternehmen" value={data.position || ''} onChange={(e) => onChange('position', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Gesellschaftsform</InputLabel>
          <Select value={data.society_form || ''} onChange={(e) => onChange('society_form', e.target.value)} label="Gesellschaftsform">
            <MenuItem value="">–</MenuItem>
            {GESELLSCHAFTSFORMEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Straße / Nr." value={data.strasse || ''} onChange={(e) => onChange('strasse', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="E-Mail *" type="email" value={data.contact_email || ''} onChange={(e) => onChange('contact_email', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Telefon" value={data.contact_phone || ''} onChange={(e) => onChange('contact_phone', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField fullWidth size="small" label="Homepage" value={data.homepage || ''} onChange={(e) => onChange('homepage', e.target.value)} placeholder="https://www.example.de" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Land (Firmensitz)</InputLabel>
          <Select value={data.land || 'Deutschland'} onChange={(e) => onChange('land', e.target.value)} label="Land (Firmensitz)">
            <MenuItem value="Deutschland">Deutschland</MenuItem>
            <MenuItem value="Österreich">Österreich</MenuItem>
            <MenuItem value="Schweiz">Schweiz</MenuItem>
            <MenuItem value="EU-Ausland">EU-Ausland</MenuItem>
            <MenuItem value="International">International</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </>
  );

  const renderLeadSection = (data: Record<string, any>, onChange: (f: string, v: any) => void) => (
    <>
      <SectionTitle>6. Lead-Einstellungen</SectionTitle>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Lead-Preis (EUR)" type="number" value={data.lead_price || ''} onChange={(e) => onChange('lead_price', e.target.value)} inputProps={{ min: 0, step: 0.01 }} placeholder="z.B. 49.99" />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="Max. Verkäufe" type="number" value={data.purchase_limit || '4'} onChange={(e) => onChange('purchase_limit', e.target.value)} inputProps={{ min: 1, step: 1 }} helperText={selected ? `Bereits ${selected.purchases_count ?? 0} mal verkauft` : 'Wie viele Dienstleister dürfen kaufen?'} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select value={data.status || 'pending'} onChange={(e) => onChange('status', e.target.value)} label="Status">
            <MenuItem value="approved">Veröffentlicht</MenuItem>
            <MenuItem value="pending">Ausstehend (nicht veröffentlicht)</MenuItem>
            <MenuItem value="rejected">Abgelehnt</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth size="small" multiline rows={3} label="Anmerkung des Portals" value={data.portal_note || ''} onChange={(e) => onChange('portal_note', e.target.value)} helperText="Hinweise für Dienstleister, sichtbar in der Ausschreibungsdetailseite" placeholder="z.B. Bitte nur Dienstleister mit DSGVO-Zertifizierung…" />
      </Grid>
    </>
  );

  return (
    <PortalLayout>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">Ausschreibungen</Typography>
        <Stack direction="row" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="pending">Ausstehend</MenuItem>
              <MenuItem value="approved">Genehmigt</MenuItem>
              <MenuItem value="rejected">Abgelehnt</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setCreateForm(defaultCreateForm); setCreateOpen(true); }}
            sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Neuer Auftrag
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : inquiries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">Keine Anfragen vorhanden.</Typography>
        </Paper>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titel</TableCell>
              <TableCell>Kontakt</TableCell>
              <TableCell>Richtung</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Verkäufe</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Eingegangen</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inquiries.map((inq) => (
              <TableRow key={inq.id} hover>
                <TableCell sx={{ fontWeight: 600, maxWidth: 200 }}>
                  <Typography variant="body2" noWrap title={inq.title}>{inq.title}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{inq.company_name || inq.contact_name || '–'}</Typography>
                  <Typography variant="caption" color="textSecondary">{inq.contact_email}</Typography>
                </TableCell>
                <TableCell>
                  {inq.direction && (
                    <Chip label={inq.direction === 'inbound' ? 'Inbound' : inq.direction === 'both' ? 'Beides' : 'Outbound'} size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>{inq.city ? `${inq.zip_code || ''} ${inq.city}` : (inq.region || '–')}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: parseInt(inq.purchases_count) >= (inq.purchase_limit || 4) ? '#c62828' : '#2e7d32' }}>
                    {parseInt(inq.purchases_count) || 0}/{inq.purchase_limit || 4}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <Chip label={STATUS_LABEL[inq.status] || inq.status} color={STATUS_COLOR[inq.status] || 'default'} size="small" />
                    {inq.phone_qualified && <VerifiedUserIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                  </Stack>
                </TableCell>
                <TableCell>{new Date(inq.created_at).toLocaleDateString('de-DE')}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" onClick={() => handleOpen(inq)}>
                    Bearbeiten
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
              Ausschreibung #{selected.id} bearbeiten
              <Chip label={STATUS_LABEL[selected.status] || selected.status} color={STATUS_COLOR[selected.status] || 'default'} size="small" sx={{ ml: 1.5 }} />
            </DialogTitle>
            <DialogContent dividers>
              {selected.status === 'pending' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Diese Anfrage ist nicht veröffentlicht und wartet auf Freigabe. Daten speichern und dann Freigabe-Entscheidung treffen.
                </Alert>
              )}
              <Grid container spacing={2} sx={{ pt: 1 }}>
                {renderProjectTypeSection(editData, setField)}
                {renderProjectDetailsSection(editData, setField)}
                {renderEnrichmentSection(editData, setField)}
                {renderDescriptionSection(editData, setField)}
                {renderContactSection(editData, setField)}
                {renderLeadSection(editData, setField)}

                {purchases.length > 0 && (
                  <>
                    <SectionTitle mt={2}>{`Käufer (${purchases.length}/${editData.purchase_limit || 4})`}</SectionTitle>
                    <Grid size={{ xs: 12 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f6fa' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Dienstleister</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Unternehmen</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>E-Mail</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Kaufdatum</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Preis</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {purchases.map((p) => (
                            <TableRow key={p.id}>
                              <TableCell sx={{ fontSize: '0.82rem' }}>{p.contractor_name}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem' }}>{p.contractor_company || '–'}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem' }}>{p.contractor_email}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem' }}>{new Date(p.purchased_at).toLocaleDateString('de-DE')}</TableCell>
                              <TableCell sx={{ fontSize: '0.82rem' }}>€{parseFloat(p.purchase_price || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Grid>
                  </>
                )}

                {selected.status === 'pending' && (
                  <>
                    <SectionTitle mt={2}>7. Freigabe-Entscheidung</SectionTitle>
                    <Grid size={{ xs: 12 }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Entscheidung</InputLabel>
                        <Select value={approveDecision} onChange={(e) => setApproveDecision(e.target.value)} label="Entscheidung">
                          <MenuItem value="approved">Genehmigen & veröffentlichen</MenuItem>
                          <MenuItem value="rejected">Ablehnen</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => setDialogOpen(false)}>Schließen</Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? <CircularProgress size={18} /> : 'Daten speichern'}
              </Button>
              {selected.status === 'pending' && (
                <Button
                  variant="contained"
                  color={approveDecision === 'approved' ? 'success' : 'error'}
                  onClick={handleApprove}
                  disabled={isSaving}
                  sx={approveDecision === 'approved' ? { background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 } : {}}
                >
                  {isSaving ? <CircularProgress size={18} color="inherit" /> : approveDecision === 'approved' ? 'Speichern & Genehmigen' : 'Speichern & Ablehnen'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Neuer Auftrag erstellen</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {renderProjectTypeSection(createForm, handleCreateField)}
            {renderProjectDetailsSection(createForm, handleCreateField)}
            {renderEnrichmentSection(createForm, handleCreateField)}
            {renderDescriptionSection(createForm, handleCreateField)}
            {renderContactSection(createForm, handleCreateField)}
            {renderLeadSection({ ...createForm, purchase_limit: createForm.purchase_limit ?? '4' }, handleCreateField)}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={isCreating}
            sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
          >
            {isCreating ? <CircularProgress size={20} color="inherit" /> : 'Auftrag erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </PortalLayout>
  );
};

export default AdminInquiriesPage;
